import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { RELATIONS, AGE_GROUPS, INTERESTS, BUDGETS, GENDERS, getVasteProducten } from "../constants";
import { Events } from "../analytics";

export default function Home() {
  const location = useLocation();
  const preload = location.state?.preload || null;
  const startStap = location.state?.startStap || 1;

  const [stap, setStap] = useState(preload ? startStap : 1);
  const [data, setData] = useState(preload || {});

  const update = (nieuw, door = false) => {
    const bijgewerkt = { ...data, ...nieuw };
    setData(bijgewerkt);
    if (door) setStap(s => s + 1);
  };
  const terug = () => setStap(s => s - 1);
  const opnieuw = () => { setData({}); setStap(1); };

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stap]);

  return (
    <main className="main">
      {/* Hero — altijd zichtbaar op stap 1, verdwijnt daarna */}
      {stap === 1 && (
        <div className="hero">
          <div className="hero-tag">✨ Gratis · Altijd persoonlijk</div>
          <h1>Nooit meer een cadeau<br/>vergeten — en altijd<br/>het <em>perfecte</em> cadeau</h1>
          <p>
            Givn onthoudt de mensen die je wilt verrassen en stuurt je op het juiste moment een herinnering — met cadeausuggesties die echt passen bij wie ze zijn.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 28 }}>
            {[
              { e: "🎯", t: "Persoonlijk op maat" },
              { e: "📅", t: "Agendaherinnering" },
              { e: "🎁", t: "Direct bestelbaar" },
            ].map(f => (
              <div key={f.t} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "rgba(255,255,255,0.15)", borderRadius: 50,
                padding: "6px 14px", fontSize: 13, color: "white",
              }}>
                <span>{f.e}</span> {f.t}
              </div>
            ))}
          </div>
          <a href="#start" className="hero-cta"
            onClick={e => { e.preventDefault(); document.querySelector(".card")?.scrollIntoView({ behavior: "smooth" }); }}>
            Voeg iemand toe — duurt 2 minuten →
          </a>
        </div>
      )}
      <div className="card" id="start">
        {stap >= 1 && stap <= 3 && (
          <div className="progress">
            {[1, 2, 3].map(i => (
              <div key={i} className="pbar" style={{ background: i <= stap ? "#C9856A" : "#EDE3D9" }} />
            ))}
          </div>
        )}
        {stap === 1 && <Stap1 data={data} update={update} terug={() => setStap(1)} />}
        {stap === 2 && <Stap2 data={data} update={update} terug={terug} />}
        {stap === 3 && <Stap3 data={data} update={update} terug={terug} />}
        {stap === 4 && <StapMail data={data} terug={terug} opnieuw={opnieuw} />}
      </div>
    </main>
  );
}

// ── Stap 1: voor wie is het cadeau? ───────────────────────────────────
function Stap1({ data, update }) {
  const [naam, setNaam] = useState(data.naam || "");
  const [email, setEmail] = useState(data.koperEmail || "");
  const ok = naam.trim() && data.ageGroup && data.gender && data.relation && email.includes("@");

  return (
    <div className="fi">
      <div className="tag">Stap 1 van 3</div>
      <h1 className="step">Voor wie zoek je<br/>een <em>cadeau</em>?</h1>
      <p className="sub">Givn zoekt suggesties die écht passen bij deze persoon.</p>

      <div className="flabel">Naam</div>
      <input className="inp" placeholder="bijv. Emma" value={naam}
        onChange={e => setNaam(e.target.value)} autoFocus />

      <div className="flabel">Leeftijd</div>
      <div className="chips">
        {AGE_GROUPS.map(a => (
          <div key={a.id} className={`age-chip ${data.ageGroup === a.id ? "sel" : ""}`}
            onClick={() => update({ ageGroup: a.id })}>
            <span className="age-m">{a.l}</span>
            <span className="age-s">{a.s}</span>
          </div>
        ))}
      </div>

      <div className="flabel">Geslacht</div>
      <div className="chips">
        {GENDERS.map(g => (
          <div key={g.id} className={`chip ${data.gender === g.id ? "sel" : ""}`}
            onClick={() => update({ gender: g.id })}>
            <span>{g.e}</span>{g.l}
          </div>
        ))}
      </div>

      <div className="flabel">Relatie</div>
      <div className="chips">
        {RELATIONS.map(r => (
          <div key={r.id} className={`chip ${data.relation === r.id ? "sel" : ""}`}
            onClick={() => update({ relation: r.id })}>
            <span>{r.e}</span>{r.l}
          </div>
        ))}
      </div>

      <div className="flabel">Jouw e-mailadres</div>
      <input className="inp" placeholder="jouw@email.nl" type="email" value={email}
        onChange={e => setEmail(e.target.value)} style={{ marginBottom: 18 }} />
      <p className="sub" style={{ fontSize: 12, marginTop: -10, marginBottom: 16 }}>
        Voor je herinneringsmail en de agendaknop.
      </p>

      <button className={`bfull bn ${ok ? "on" : "off"}`}
        onClick={() => { if(ok) { Events.STAP_VOLTOOID(1); update({ naam: naam.trim(), koperEmail: email }, true); } }}>
        Verder →
      </button>
    </div>
  );
}

// ── Stap 2: 2 interesses + optioneel vrij veld ───────────────────
function Stap2({ data, update, terug }) {
  const sel = data.interests || [];
  const [extraTekst, setExtraTekst] = useState(data.interestsExtra || "");
  const toggle = id => {
    const next = sel.includes(id) ? sel.filter(x => x !== id) : sel.length < 2 ? [...sel, id] : sel;
    update({ interests: next });
  };
  const ok = sel.length === 2;

  return (
    <div className="fi">
      <div className="tag">Stap 2 van 3</div>
      <h1 className="step">
        {data.ontvangerType === "stel"
          ? <>Wat vinden <em>{data.naam || "zij"}</em> leuk?</>
          : data.ontvangerType === "groep"
          ? <>Wat past bij <em>{data.naam || "de groep"}</em>?</>
          : <>Wat vindt <em>{data.naam || "deze persoon"}</em> leuk?</>}
      </h1>
      <p className="sub">
        Kies precies <strong>2 interesses</strong>.
        {ok && <span style={{ color: "#3D5A4C", fontWeight: 600 }}> ✓ Perfect!</span>}
      </p>
      <div className="chips">
        {INTERESTS.map(i => (
          <div key={i.id} className={`chip ${sel.includes(i.id) ? "sel" : ""}`}
            onClick={() => toggle(i.id)}>
            <span>{i.e}</span>{i.l}
          </div>
        ))}
      </div>

      {/* Optioneel vrij invulveld */}
      <div style={{ marginBottom: 14 }}>
        <div className="flabel" style={{ marginBottom: 6 }}>
          Iets specifieks toevoegen? <span style={{ color: "#8A7B73", fontWeight: 400 }}>(optioneel)</span>
        </div>
        <input
          className="inp"
          placeholder={`bijv. "houdt van Japanse whisky" of "is gek op klimmen"`}
          value={extraTekst}
          onChange={e => { setExtraTekst(e.target.value); update({ interestsExtra: e.target.value }); }}
          style={{ marginBottom: 0 }}
        />
      </div>

      {/* Deelknop */}
      <DeelBanner data={data} />

      <div className="brow">
        <button className="bbk" onClick={terug}>← Terug</button>
        <button className={`bn ${ok ? "on" : "off"}`} onClick={() => { if(ok) { Events.STAP_VOLTOOID(2); update({ interestsExtra: extraTekst }, true); } }}>Verder →</button>
      </div>
    </div>
  );
}

// ── Deelbanner: verschijnt in stap 2 ─────────────────────────────
function DeelBanner({ data }) {
  const [stap, setStap] = useState("idle"); // idle | laden | klaar | fout
  const [link, setLink] = useState(null);
  const [gekopieerd, setGekopieerd] = useState(false);

  const maakLink = async () => {
    if (stap === "laden" || stap === "klaar") return;
    setStap("laden");
    try {
      const res = await fetch("/.netlify/functions/share-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          naam: data.naam || "Iemand",
          gender: data.gender || "nvt",
          ageGroup: data.ageGroup || "dertig",
          relation: data.relation || "vriend",
          koperEmail: data.koperEmail || null,
        }),
      });
      const json = await res.json();
      if (json.url) {
        setLink(json.url);
        setStap("klaar");
        Events.DEELLINK_AANGEMAAKT();
      } else {
        console.error("share-link: geen url in response", json);
        setStap("fout");
      }
    } catch (e) {
      console.error("share-link fetch error:", e);
      setStap("fout");
    }
  };

  const waText = encodeURIComponent(
    `Hey! Ik wil je een cadeau geven — maar ik wil zeker weten wat jij leuk vindt 🎁\n\nKan je even dit invullen? Duurt nog geen minuut:\n${link || ""}`
  );

  return (
    <div style={{
      background: "#FDEEE8", border: "1.5px solid #EDE3D9",
      borderRadius: 16, padding: 16, marginBottom: 14,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#2C2420", marginBottom: 4 }}>
        🤔 Weet je niet wat {data.naam || "deze persoon"} leuk vindt?
      </div>
      <div style={{ fontSize: 12, color: "#8A7B73", lineHeight: 1.6, marginBottom: 12 }}>
        Stuur een persoonlijke link — {data.naam || "hij/zij"} vult zelf zijn interesses in. Jij krijgt een seintje zodra het klaar is.
      </div>

      {stap === "idle" && (
        <button onClick={maakLink} style={{
          width: "100%", padding: "11px 0", borderRadius: 50,
          border: "1.5px solid #C9856A", background: "white",
          color: "#C9856A", fontWeight: 700, fontSize: 13, cursor: "pointer",
          fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.01em",
        }}>
          🔗 Maak deellink aan
        </button>
      )}

      {stap === "laden" && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 0", fontSize: 13, color: "#8A7B73" }}>
          <div style={{ width: 14, height: 14, border: "2px solid #EDE3D9", borderTopColor: "#C9856A", borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
          Deellink aanmaken…
        </div>
      )}

      {stap === "fout" && (
        <div>
          <div style={{ fontSize: 12, color: "#C9856A", marginBottom: 8, textAlign: "center" }}>
            Kon link niet aanmaken. Controleer je verbinding en probeer opnieuw.
          </div>
          <button onClick={() => setStap("idle")} style={{
            width: "100%", padding: "10px 0", borderRadius: 50,
            border: "1.5px solid #C9856A", background: "white",
            color: "#C9856A", fontWeight: 700, fontSize: 13, cursor: "pointer",
            fontFamily: "'DM Sans',sans-serif",
          }}>
            Opnieuw proberen
          </button>
        </div>
      )}

      {stap === "klaar" && link && (
        <div>
          {/* Link preview */}
          <div style={{
            background: "white", border: "1px solid #EDE3D9", borderRadius: 10,
            padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#8A7B73",
            wordBreak: "break-all", fontFamily: "monospace",
          }}>
            {link}
          </div>

          {/* Deel knoppen — social share stijl */}
          <div style={{ display: "flex", gap: 8 }}>
            {/* WhatsApp */}
            <a
              href={"https://api.whatsapp.com/send?text=" + waText}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => Events.WHATSAPP_GEDEELD()}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                gap: 6, padding: "11px 8px", borderRadius: 12,
                background: "#25D366", color: "white",
                fontWeight: 700, fontSize: 13, textDecoration: "none",
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>

            {/* Kopieer */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(link).then(() => {
                  setGekopieerd(true);
                  setTimeout(() => setGekopieerd(false), 2500);
                  Events.LINK_GEKOPIEERD();
                });
              }}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                gap: 6, padding: "11px 8px", borderRadius: 12,
                border: "1.5px solid #EDE3D9",
                background: gekopieerd ? "#EDF3EF" : "white",
                color: gekopieerd ? "#3D5A4C" : "#2C2420",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                transition: "all 0.2s",
              }}
            >
              {gekopieerd ? "✓ Gekopieerd!" : "📋 Kopieer link"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stap 3: event + datum + budget ────────────────────────────────
function Stap3({ data, update, terug }) {
  const [type, setType] = useState(data.eventType || "");
  const [datum, setDatum] = useState(data.eventDatum || "");
  const ok = data.budget && datum && type.trim();

  return (
    <div className="fi">
      <div className="tag">Stap 3 van 3</div>
      <h1 className="step">Wanneer is het <em>event</em>?</h1>
      <p className="sub">We sturen je de suggesties per mail met een agenda herinnering erbij.</p>

      <div className="flabel">Type event</div>
      <input className="inp" placeholder="bijv. Verjaardag, Kerst, Jubileum…" value={type}
        onChange={e => { setType(e.target.value); update({ eventType: e.target.value }); }} />

      <div className="flabel">Datum</div>
      <input className="inp inp-rect" type="date" value={datum}
        min={new Date().toISOString().split("T")[0]}
        onChange={e => { setDatum(e.target.value); update({ eventDatum: e.target.value }); }}
        style={{ marginBottom: 18 }} />

      <div className="flabel">Budget</div>
      <div className="bgrid">
        {BUDGETS.map(b => (
          <div key={b.id} className={`bcard ${data.budget === b.id ? "sel" : ""}`}
            onClick={() => update({ budget: b.id })}>
            <div className="bl">{b.l}</div>
            <div className="bs">{b.s}</div>
          </div>
        ))}
      </div>

      <div className="brow">
        <button className="bbk" onClick={terug}>← Terug</button>
        <button className={`bn ${ok ? "gr" : "off"}`}
          onClick={() => { if(ok) { Events.STAP_VOLTOOID(4, { budget: data.budget }); update({ eventType: type.trim(), eventDatum: datum }, true); } }}>
          Bekijk suggesties ✓
        </button>
      </div>
    </div>
  );
}

// ── Stap 5: suggesties + mail ─────────────────────────────────────
function StapMail({ data, terug, opnieuw }) {
  const [email, setEmail] = useState(data.koperEmail || "");
  const [loading, setLoading] = useState(false);
  const [verzonden, setVerzonden] = useState(false);
  const [fout, setFout] = useState("");
  const [producten, setProducten] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);

  const rel  = RELATIONS.find(r => r.id === data.relation);
  const ints = (data.interests || []).map(id => INTERESTS.find(i => i.id === id)).filter(Boolean);
  const datumStr = data.eventDatum
    ? new Date(data.eventDatum).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })
    : "";

  useEffect(() => {
    async function fetchSuggesties() {
      try {
        const res = await fetch("/.netlify/functions/send-suggestions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, alleenSuggesties: true }),
        });
        if (!res.ok) throw new Error();
        const json = await res.json();
        if (json.producten?.length > 0) {
          Events.SUGGESTIES_GELADEN("ai");
          setProducten(json.producten);
        } else {
          Events.SUGGESTIES_GELADEN("fallback");
          setProducten(getVasteProducten(data.interests?.[0], data.interests?.[1], data.lifeEvent, data.budget));
        }
      } catch {
        Events.SUGGESTIES_GELADEN("fallback");
        setProducten(getVasteProducten(data.interests?.[0], data.interests?.[1], data.lifeEvent, data.budget));
      } finally {
        setAiLoading(false);
      }
    }
    fetchSuggesties();
  }, []);

  const verstuur = async () => {
    if (!email.includes("@")) { setFout("Vul een geldig e-mailadres in."); return; }
    setFout(""); setLoading(true);
    try {
      const res = await fetch("/.netlify/functions/send-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, email, producten }),
      });
      if (!res.ok) throw new Error("Versturen mislukt");
      Events.MAIL_VERZONDEN(data.budget, data.relation);
      setVerzonden(true);
    } catch {
      setFout("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="fi">
      {/* Profiel banner */}
      <div className="rbanner">
        <span style={{ fontSize: 36 }}>🎁</span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "white", marginBottom: 3 }}>
            Suggesties voor {data.naam} klaar!
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
            {rel?.e} {rel?.l} · {ints.map(i => `${i?.e} ${i?.l}`).join(" · ")}
          </div>
          {datumStr && (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
              📅 {data.eventType} op {datumStr}
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: "#8A7B73", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>
        Cadeausuggesties voor {data.naam}
      </div>

      {aiLoading ? (
        <div className="ai-loader">
          <div className="ai-spinner" />
          <div style={{ fontSize: 14, color: "#8A7B73" }}>Suggesties op maat samenstellen…</div>
        </div>
      ) : (
        producten?.map((p, i) => (
          <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
            className="pcard" style={{ animationDelay: `${i * 0.07}s` }}
            onClick={() => Events.PRODUCT_KLIK(p.naam, p.winkel)}>
            <div className="pico">{p.emoji}</div>
            <div className="pinfo">
              <div className="pnm">{p.naam}</div>
              <div className="pmeta"><span className="pp">{p.prijs}</span> · {p.winkel}</div>
            </div>
            <span className="pbtn">Bekijk →</span>
          </a>
        ))
      )}

      <div className="div" />


      {/* Mailbox */}
      {!verzonden ? (
        <div className="mailbox">
          <div className="mailt">📬 Stuur me dit ook per mail</div>
          <div className="mails">
            Je ontvangt de suggesties per mail en kunt ze daar direct toevoegen aan je agenda.
            Je krijgt <strong>14 dagen vóór {data.eventType || "het event"}</strong> automatisch een herinnering met de productlinks.
          </div>
          <input className="inp" placeholder="jouw@email.nl" type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && verstuur()}
            style={{ marginBottom: 10 }} />
          <button
            className={`bfull bn ${loading ? "loading" : email.includes("@") ? "gr" : "off"}`}
            onClick={verstuur} disabled={loading}>
            {loading ? "Versturen…" : "✉ Verstuur bevestigingsmail"}
          </button>
          {fout && <div className="err">{fout}</div>}
        </div>
      ) : (
        <div className="mailbox" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#2C2420", marginBottom: 5 }}>
            Mail onderweg naar {email}
          </div>
          <div style={{ fontSize: 13, color: "#8A7B73", lineHeight: 1.6, marginBottom: 16 }}>
            Klik in de mail op de agenda knop van jouw keuze — het event staat er direct in,
            inclusief herinnering 14 dagen van tevoren met de productlinks.
          </div>
          {/* BUG 4: + Nog iemand toevoegen pas NA verzenden */}
          <button className="bfull bn on" onClick={opnieuw}>
            + Nog iemand toevoegen
          </button>
        </div>
      )}
    </div>
  );
}
